import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { api, Coach } from "../api/client";
import { coachSalaryApi, CoachPayment, Earnings, SalaryHistoryEntry } from "../api/coachSalary";
import PageLoader from "../components/PageLoader";
import "./CoachSalaryPage.css";

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const MONTHS_BACK = 5;

const pad = (n: number) => String(n).padStart(2, "0");

// Today's date in the admin's time zone, as the server's date strings are.
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function shiftMonth(month: string, delta: number): string {
  const [year, mm] = month.split("-").map(Number);
  const index = year * 12 + (mm - 1) + delta;
  return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;
}

function lastDayOf(month: string): string {
  const [year, mm] = month.split("-").map(Number);
  return `${month}-${pad(new Date(year, mm, 0).getDate())}`;
}

// "2026-09-02" → "02.09.2026", from the string: new Date() would shift the day
// west of Greenwich.
function formatDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function formatMonth(month: string): string {
  const [year, mm] = month.split("-");
  const name = MONTHS[Number(mm) - 1];
  return name ? `${name} ${year}` : month;
}

// A rate change is a moment in time, so it is shown in the admin's zone.
function formatMoment(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const moneyFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });
const formatMoney = (value: number) => `${moneyFormat.format(value)} ₽`;

function parseMoney(text: string): number | null {
  const normalized = text.replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Number(normalized);
}

function trainingsWord(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  if (ones === 1 && tens !== 11) return "тренировка";
  if (ones >= 2 && ones <= 4 && (tens < 12 || tens > 14)) return "тренировки";
  return "тренировок";
}

const isDateString = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const byName = (a: Coach, b: Coach) => a.name.localeCompare(b.name, "ru");

type Account = {
  history: SalaryHistoryEntry[];
  payments: CoachPayment[];
  // Earned per "YYYY-MM", counting trainings up to today.
  earned: Record<string, number>;
  months: string[];
};

export default function CoachSalaryPage() {
  const today = localToday();
  const currentMonth = today.slice(0, 7);

  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [coachID, setCoachID] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState("");
  const [account, setAccount] = useState<Account | null>(null);
  const [from, setFrom] = useState(`${currentMonth}-01`);
  const [to, setTo] = useState(() => lastDayOf(currentMonth));
  const [period, setPeriod] = useState<Earnings | null>(null);
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [paidInput, setPaidInput] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const loadCoaches = useCallback(async () => {
    try {
      const list = ((await api.listCoaches()) ?? []).slice().sort(byName);
      setCoaches(list);
      setCoachID((id) => (id !== null && list.some((c) => c.id === id) ? id : list[0]?.id ?? null));
    } catch (err: any) {
      console.error("Failed to load coaches", err);
      setError(err?.message || "Не удалось загрузить тренеров");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoaches();
  }, [loadCoaches]);

  const coach = coaches.find((c) => c.id === coachID) ?? null;
  const currentRate = coach?.salary_per_training;

  useEffect(() => {
    if (currentRate !== undefined) setRate(String(currentRate));
  }, [coachID, currentRate]);

  useEffect(() => {
    setAccount(null);
    setPeriod(null);
    setEditingMonth(null);
  }, [coachID]);

  useEffect(() => {
    if (coachID === null) return;
    let cancelled = false;
    (async () => {
      try {
        const [history, payments] = await Promise.all([
          coachSalaryApi.listSalaryHistory(coachID),
          coachSalaryApi.listPayments(coachID),
        ]);
        const paid = payments ?? [];
        const start = paid.reduce(
          (earliest, p) => (p.month_year < earliest ? p.month_year : earliest),
          shiftMonth(currentMonth, -MONTHS_BACK)
        );
        const earnings = await coachSalaryApi.getEarnings(coachID, `${start}-01`, today);
        if (cancelled) return;
        const earned: Record<string, number> = {};
        for (const s of earnings.sessions ?? []) {
          const month = s.date.slice(0, 7);
          earned[month] = (earned[month] ?? 0) + s.salary;
        }
        const months: string[] = [];
        for (let m = currentMonth; m >= start; m = shiftMonth(m, -1)) {
          if (m === currentMonth || earned[m] || paid.some((p) => p.month_year === m)) months.push(m);
        }
        setAccount({ history: history ?? [], payments: paid, earned, months });
      } catch (err: any) {
        if (cancelled) return;
        console.error("Failed to load coach salary", err);
        setError(err?.message || "Не удалось загрузить зарплату тренера");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coachID, reloadKey, currentMonth, today]);

  const periodValid = isDateString(from) && isDateString(to) && from <= to;

  useEffect(() => {
    if (coachID === null || !periodValid) return;
    let cancelled = false;
    coachSalaryApi
      .getEarnings(coachID, from, to)
      .then((result) => {
        if (!cancelled) setPeriod(result);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error("Failed to load coach earnings", err);
        setError(err?.message || "Не удалось загрузить заработок");
      });
    return () => {
      cancelled = true;
    };
  }, [coachID, from, to, periodValid, reloadKey]);

  const rateValue = parseMoney(rate);
  const canSaveRate = !busy && coach !== null && rateValue !== null && rateValue !== coach.salary_per_training;

  const saveRate = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSaveRate || !coach || rateValue === null) return;
    setBusy(true);
    setError(null);
    try {
      await coachSalaryApi.setRate(coach.id, rateValue);
      await loadCoaches();
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      console.error("Coach rate save failed", err);
      setError(err?.message || "Не удалось сохранить ставку");
    } finally {
      setBusy(false);
    }
  };

  const startEditingPaid = (month: string, payment: CoachPayment | undefined) => {
    setError(null);
    setEditingMonth(month);
    setPaidInput(payment ? String(payment.paid_amount) : "");
  };

  const paidValue = parseMoney(paidInput);

  const savePaid = async (month: string) => {
    if (coachID === null || paidValue === null) return;
    setBusy(true);
    setError(null);
    try {
      await coachSalaryApi.setPaid(coachID, month, paidValue);
      setEditingMonth(null);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      console.error("Coach payment save failed", err);
      setError(err?.message || "Не удалось сохранить выплату");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageLoader />;

  const sessions = (period?.sessions ?? []).slice().sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="page page-wide">
      <h2>Зарплата тренеров</h2>
      <p className="page-description">
        Внутренний учёт: ставка за тренировку, заработок и выплаты тренерам. Каждая тренировка
        считается по ставке, которая действовала в её день, поэтому новая ставка не меняет прошлые
        суммы.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {coaches.length === 0 ? (
        <p className="mav-status-muted">{error ? "—" : "Тренеров пока нет. Добавьте их в разделе «Тренеры»."}</p>
      ) : (
        <div className="salary-toolbar">
          <label className="cp-field">
            <span>Тренер</span>
            <select
              className="mav-input"
              value={coachID ?? ""}
              disabled={busy}
              onChange={(e) => setCoachID(Number(e.target.value))}
            >
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {coach && !account && !error && <PageLoader />}

      {coach && account && (
        <>
          <section className="salary-section" aria-labelledby="salary-rate-title">
            <h3 id="salary-rate-title">Ставка</h3>
            <div className="salary-rate-layout">
              <form className="mav-card cp-form salary-rate-form" aria-label="Ставка за тренировку" onSubmit={saveRate}>
                <div className="cp-field">
                  <label className="cp-field">
                    <span>Ставка за тренировку, ₽</span>
                    <input
                      className={rateValue === null ? "mav-input has-error" : "mav-input"}
                      inputMode="decimal"
                      autoComplete="off"
                      value={rate}
                      disabled={busy}
                      onChange={(e) => setRate(e.target.value)}
                    />
                  </label>
                  <span className="mav-hint">
                    Новая ставка действует с сегодняшних тренировок. Прошедшие остаются по прежней.
                  </span>
                </div>
                <div className="cp-actions">
                  <button type="submit" className="mav-btn-primary" disabled={!canSaveRate}>
                    Сохранить ставку
                  </button>
                </div>
              </form>

              {account.history.length === 0 ? (
                <div className="salary-warning">
                  Истории ставок нет, поэтому сервер считает тренировки этого тренера по 0 ₽. Запись в
                  истории появится, только когда ставка изменится, и будет действовать с сегодняшнего
                  дня — прошедшие тренировки останутся по 0 ₽.
                </div>
              ) : (
                <div className="table-wrap salary-history">
                  <table className="settings-table" aria-label="История ставок">
                    <thead>
                      <tr>
                        <th>Действует с</th>
                        <th>Ставка</th>
                      </tr>
                    </thead>
                    <tbody>
                      {account.history.map((h, i) => (
                        <tr key={h.id}>
                          <td className="date-cell">{formatMoment(h.effective_from)}</td>
                          <td className="salary-amount">
                            {formatMoney(h.salary)}
                            {i === 0 && <span className="mav-badge mav-badge-active">Текущая</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="salary-section" aria-labelledby="salary-payments-title">
            <h3 id="salary-payments-title">Выплаты по месяцам</h3>
            <p className="page-description">
              Заработано — по тренировкам с датой не позже сегодняшней. Сумма выплаты заменяет
              записанную раньше, а не прибавляется к ней.
            </p>
            <div className="table-wrap">
              <table className="settings-table stack-table salary-months" aria-label="Выплаты по месяцам">
                <thead>
                  <tr>
                    <th>Месяц</th>
                    <th>Заработано</th>
                    <th>Выплачено</th>
                    <th>Итог</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {account.months.map((month) => {
                    const earned = account.earned[month] ?? 0;
                    const payment = account.payments.find((p) => p.month_year === month);
                    const diff = Math.round(((payment?.paid_amount ?? 0) - earned) * 100) / 100;
                    const label = formatMonth(month);
                    return (
                      <tr key={month}>
                        <td className="cell-salary-month">{label}</td>
                        <td className="cell-salary-earned">{formatMoney(earned)}</td>
                        {editingMonth === month ? (
                          <>
                            <td className="cell-salary-paid" colSpan={2}>
                              <input
                                className={paidValue === null ? "mav-input has-error" : "mav-input"}
                                aria-label={`Выплачено за ${label}`}
                                inputMode="decimal"
                                autoComplete="off"
                                value={paidInput}
                                disabled={busy}
                                onChange={(e) => setPaidInput(e.target.value)}
                              />
                            </td>
                            <td className="cell-action">
                              <div className="row-actions">
                                <button className="mav-btn-primary" disabled={busy || paidValue === null} onClick={() => savePaid(month)}>
                                  Сохранить
                                </button>
                                <button className="mav-btn-secondary" disabled={busy} onClick={() => setEditingMonth(null)}>
                                  Отмена
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="cell-salary-paid">
                              {payment ? formatMoney(payment.paid_amount) : <span className="mav-status-muted">Не отмечено</span>}
                            </td>
                            <td className="cell-salary-result">
                              {earned === 0 && !payment ? (
                                <span className="mav-status-muted">—</span>
                              ) : diff === 0 ? (
                                <span className="mav-badge mav-badge-active">Выплачено полностью</span>
                              ) : diff < 0 ? (
                                <span className="salary-due">Осталось {formatMoney(-diff)}</span>
                              ) : (
                                <span className="salary-over">Переплата {formatMoney(diff)}</span>
                              )}
                            </td>
                            <td className="cell-action">
                              <div className="row-actions">
                                <button
                                  className="mav-btn-secondary"
                                  disabled={busy || editingMonth !== null}
                                  onClick={() => startEditingPaid(month, payment)}
                                >
                                  Изменить
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="salary-section" aria-labelledby="salary-period-title">
            <h3 id="salary-period-title">Заработок за период</h3>
            <div className="salary-period-filters">
              <label className="cp-field">
                <span>С</span>
                <input className="mav-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label className="cp-field">
                <span>По</span>
                <input className="mav-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
            </div>
            {!periodValid ? (
              <p className="mav-hint is-error">
                {isDateString(from) && isDateString(to) ? "Начало периода позже конца" : "Укажите обе даты"}
              </p>
            ) : (
              period && (
                <div className="table-wrap">
                  <table className="settings-table stack-table salary-sessions" aria-label="Тренировки за период">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Тренировка</th>
                        <th>Ставка</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="empty-cell">
                            За этот период тренировок нет
                          </td>
                        </tr>
                      ) : (
                        sessions.map((s) => (
                          <tr key={s.training_id}>
                            <td className="date-cell cell-session-date">{formatDate(s.date)}</td>
                            <td className="cell-session-title">
                              {s.title || "Тренировка"}
                              {s.date > today && <span className="mav-badge mav-badge-muted">впереди</span>}
                            </td>
                            <td className="cell-session-salary">{formatMoney(s.salary)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {sessions.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={3} className="salary-total">
                            Итого: {period.count} {trainingsWord(period.count)}, {formatMoney(period.total)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )
            )}
          </section>
        </>
      )}
    </div>
  );
}
